// @ts-strict-ignore
import { useMutation, useQuery } from "@apollo/client";
import ActionDialog from "@dashboard/components/ActionDialog";
import { Combobox } from "@dashboard/components/Combobox";
import { Select } from "@dashboard/components/Select";
import useNotifier from "@dashboard/hooks/useNotifier";
import {
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckIcon from "@material-ui/icons/Check";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ClearIcon from "@material-ui/icons/Clear";
import DescriptionIcon from "@material-ui/icons/Description";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import ImageIcon from "@material-ui/icons/Image";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import PhoneIcon from "@material-ui/icons/Phone";
import SearchIcon from "@material-ui/icons/Search";
import VideocamIcon from "@material-ui/icons/Videocam";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { Box, Button, Input, Option, Text } from "@saleor/macaw-ui-next";
import React, { useMemo, useState } from "react";

import { WHATSAPP_CAMPAIGN_CREATE_MUTATION } from "../waMutations";
import {
  CUSTOMERS_SEARCH_FOR_CAMPAIGN_QUERY,
  WHATSAPP_CAMPAIGNS_QUERY,
  WHATSAPP_MEDIA_FILES_QUERY,
  WHATSAPP_TEMPLATES_QUERY,
} from "../waQueries";
import { WhatsAppMedia, WhatsAppTemplate } from "../types";

interface CustomerNode {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  defaultBillingAddress?: {
    id: string;
    phone?: string;
  } | null;
}

interface CampaignCreateWizardProps {
  open: boolean;
  onClose: () => void;
}

export const CampaignCreateWizard: React.FC<CampaignCreateWizardProps> = ({ open, onClose }) => {
  const notify = useNotifier();

  const [name, setName] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateParams, setTemplateParams] = useState<Record<string, string>>({});
  const [headerMediaUrl, setHeaderMediaUrl] = useState("");
  const [selectedMediaId, setSelectedMediaId] = useState("none");

  // Template Search and Category Filtering
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState<string>("ALL");
  const [showPreview, setShowPreview] = useState<boolean>(true);

  // Target modes: 'single' | 'multiple' | 'all'
  const [targetMode, setTargetMode] = useState<"single" | "multiple" | "all">("multiple");
  const [selectedCustomers, setSelectedCustomers] = useState<CustomerNode[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Pagination state
  const [afterCursor, setAfterCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [pageNumber, setPageNumber] = useState(1);

  // Load templates
  const { data: tmplData, loading: templatesLoading } = useQuery(WHATSAPP_TEMPLATES_QUERY, {
    fetchPolicy: "cache-and-network",
  });
  const allTemplates: WhatsAppTemplate[] = tmplData?.whatsappTemplates || [];
  const templates: WhatsAppTemplate[] = allTemplates.filter(t => t.isActive !== false);

  const marketingCount = useMemo(
    () => templates.filter(t => t.category === "MARKETING").length,
    [templates]
  );
  const utilityCount = useMemo(
    () => templates.filter(t => t.category === "UTILITY").length,
    [templates]
  );
  const authCount = useMemo(
    () => templates.filter(t => t.category === "AUTHENTICATION").length,
    [templates]
  );

  const filteredTemplates = useMemo(() => {
    return templates.filter(t => {
      const matchesCategory =
        templateCategoryFilter === "ALL" || t.category === templateCategoryFilter;
      const q = templateSearchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        t.displayName.toLowerCase().includes(q) ||
        t.name.toLowerCase().includes(q) ||
        (t.bodyText && t.bodyText.toLowerCase().includes(q));
      return matchesCategory && matchesSearch;
    });
  }, [templates, templateCategoryFilter, templateSearchQuery]);

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
  const headerFormat = (selectedTemplate?.headerFormat || "").toUpperCase();
  const requiresHeaderMedia = ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat);

  // Load uploaded media files from dedicated bucket folder
  const { data: mediaData } = useQuery(WHATSAPP_MEDIA_FILES_QUERY, {
    fetchPolicy: "cache-and-network",
  });
  const uploadedMediaFiles: WhatsAppMedia[] = mediaData?.whatsappMediaFiles || [];

  const mediaChoices: Option[] = useMemo(() => {
    const list: Option[] = [
      { label: "Use Template Default / Approved Media", value: "none" },
      { label: "Custom External URL (Paste Link)", value: "custom" },
    ];
    uploadedMediaFiles.forEach(m => {
      list.push({
        label: `${m.fileName} [${m.mediaType}]`,
        value: m.id,
      });
    });
    return list;
  }, [uploadedMediaFiles]);

  const insertTag = (paramKey: string, tag: string) => {
    const current = templateParams[paramKey] || "";
    setTemplateParams({
      ...templateParams,
      [paramKey]: current ? `${current} ${tag}` : tag,
    });
  };

  const templateComboboxOptions: Option[] = useMemo(() => {
    if (filteredTemplates.length === 0) {
      return [{ label: "No templates match your search", value: "none" }];
    }
    return filteredTemplates.map(t => ({
      label: `${t.displayName} (${t.name})`,
      value: t.id,
    }));
  }, [filteredTemplates]);

  const selectedComboboxOption: Option | null = useMemo(() => {
    if (!selectedTemplate) return null;
    return {
      label: `${selectedTemplate.displayName} (${selectedTemplate.name})`,
      value: selectedTemplate.id,
    };
  }, [selectedTemplate]);

  // Auto-select template when available and reset on dialog open
  React.useEffect(() => {
    if (open) {
      if (filteredTemplates.length > 0 && (!selectedTemplateId || !filteredTemplates.some(t => t.id === selectedTemplateId))) {
        setSelectedTemplateId(filteredTemplates[0].id);
      }
    } else {
      setSelectedTemplateId("");
      setName("");
      setSelectedCustomers([]);
      setTemplateParams({});
      setHeaderMediaUrl("");
      setSelectedMediaId("none");
      setTemplateSearchQuery("");
      setTemplateCategoryFilter("ALL");
    }
  }, [open, filteredTemplates]);

  // Safely parse template parameters schema handling single or double encoded JSON and objects
  const parsedSchema: any[] = useMemo(() => {
    let schemaList: any[] = [];
    if (selectedTemplate?.parameterSchema) {
      let schema: any = selectedTemplate.parameterSchema;
      while (typeof schema === "string") {
        try {
          schema = JSON.parse(schema);
        } catch {
          break;
        }
      }
      if (Array.isArray(schema)) {
        schemaList = schema
          .map((item, idx) =>
            typeof item === "string"
              ? { name: item, label: item }
              : { name: item.name || item.key || `param_${idx + 1}`, label: item.label || item.name || item.text }
          )
          .filter(item => !String(item.name).toLowerCase().startsWith("header_"));
      } else if (typeof schema === "object" && schema !== null) {
        schemaList = Object.keys(schema)
          .filter(key => !key.toLowerCase().startsWith("header_"))
          .map(key => {
            const val = schema[key];
            if (typeof val === "string") {
              return { name: key, label: val };
            }
            return { name: key, label: val?.label || val?.text || key, ...val };
          });
      }
    }

    // Auto-detect positional parameters {{1}}, {{2}} from bodyText if parameterSchema was empty
    if (schemaList.length === 0 && selectedTemplate?.bodyText) {
      const matches = selectedTemplate.bodyText.match(/\{\{(\d+)\}\}/g) || [];
      const uniqueNums = Array.from(new Set(matches.map(m => m.replace(/[{}]/g, ""))));
      uniqueNums.sort((a, b) => Number(a) - Number(b));
      schemaList = uniqueNums.map(num => ({
        name: num,
        label: `Variable {{${num}}}`,
      }));
    }

    return schemaList;
  }, [selectedTemplate?.parameterSchema, selectedTemplate?.bodyText]);

  // Render live preview body with parameter substitutions
  const renderPreviewBody = () => {
    const rawText = selectedTemplate?.bodyText || "";
    if (!rawText) {
      return (
        <Typography variant="body2" style={{ fontStyle: "italic", color: "#64748b" }}>
          (No body text configured for this template)
        </Typography>
      );
    }

    const parts = rawText.split(/(\{\{[^}]+\}\})/g);
    return (
      <Typography
        variant="body2"
        style={{
          color: "#111827",
          lineHeight: 1.55,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontSize: "0.85rem",
        }}
      >
        {parts.map((part, index) => {
          const match = part.match(/^\{\{([^}]+)\}\}$/);
          if (match) {
            const varKey = match[1].trim();
            const val = templateParams[varKey] || templateParams[`param_${varKey}`] || "";

            if (val) {
              return (
                <span
                  key={index}
                  style={{
                    backgroundColor: "#dcfce7",
                    color: "#15803d",
                    fontWeight: 700,
                    padding: "1px 5px",
                    borderRadius: 4,
                    border: "1px solid #86efac",
                    margin: "0 2px",
                    display: "inline-block",
                  }}
                  title={`Parameter {{${varKey}}}`}
                >
                  {val}
                </span>
              );
            }

            return (
              <span
                key={index}
                style={{
                  backgroundColor: "#fef3c7",
                  color: "#b45309",
                  fontWeight: 700,
                  padding: "1px 5px",
                  borderRadius: 4,
                  border: "1px dashed #f59e0b",
                  margin: "0 2px",
                  display: "inline-block",
                }}
                title={`Parameter {{${varKey}}} - Needs value`}
              >
                {`{{${varKey}}}`}
              </span>
            );
          }
          return part;
        })}
      </Typography>
    );
  };

  // Customer search & pagination query
  const { data: custData, loading: searching } = useQuery(CUSTOMERS_SEARCH_FOR_CAMPAIGN_QUERY, {
    variables: {
      first: 20,
      after: afterCursor || undefined,
      filter: searchQuery.trim() ? { search: searchQuery.trim() } : {},
    },
    fetchPolicy: "network-only",
  });

  const searchedCustomers: CustomerNode[] =
    custData?.customers?.edges?.map((e: any) => e.node) || [];
  const pageInfo = custData?.customers?.pageInfo;
  const totalEligibleCount = custData?.customers?.totalCount || 0;

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
    setAfterCursor(null);
    setCursorStack([]);
    setPageNumber(1);
  };

  const handleNextPage = () => {
    if (pageInfo?.hasNextPage && pageInfo?.endCursor) {
      setCursorStack(prev => [...prev, afterCursor || ""]);
      setAfterCursor(pageInfo.endCursor);
      setPageNumber(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (cursorStack.length > 0) {
      const prevStack = [...cursorStack];
      const prevCursor = prevStack.pop() || null;
      setCursorStack(prevStack);
      setAfterCursor(prevCursor === "" ? null : prevCursor);
      setPageNumber(prev => Math.max(1, prev - 1));
    }
  };

  const [createCampaign, { loading: creating }] = useMutation(WHATSAPP_CAMPAIGN_CREATE_MUTATION, {
    refetchQueries: [{ query: WHATSAPP_CAMPAIGNS_QUERY }],
    onCompleted: data => {
      if (data.whatsappCampaignCreate?.errors?.length) {
        notify({ status: "error", text: data.whatsappCampaignCreate.errors[0].message });
      } else {
        notify({ status: "success", text: "WhatsApp campaign created in Draft" });
        onClose();
      }
    },
    onError: err => {
      notify({ status: "error", text: err.message });
    },
  });

  const toggleCustomer = (customer: CustomerNode) => {
    if (targetMode === "single") {
      setSelectedCustomers([customer]);
      return;
    }

    const exists = selectedCustomers.some(c => c.id === customer.id);
    if (exists) {
      setSelectedCustomers(selectedCustomers.filter(c => c.id !== customer.id));
    } else {
      setSelectedCustomers([...selectedCustomers, customer]);
    }
  };

  const removeCustomerById = (id: string) => {
    setSelectedCustomers(selectedCustomers.filter(c => c.id !== id));
  };

  const selectAllSearched = () => {
    const newItems = searchedCustomers.filter(
      sc => !selectedCustomers.some(c => c.id === sc.id)
    );
    setSelectedCustomers([...selectedCustomers, ...newItems]);
  };

  const clearSelected = () => {
    setSelectedCustomers([]);
  };

  const handleSubmit = () => {
    if (!name || !selectedTemplateId || selectedTemplateId === "none") {
      notify({ status: "error", text: "Campaign name and template are required" });
      return;
    }

    if (targetMode === "single" && selectedCustomers.length === 0) {
      notify({ status: "error", text: "Please select a customer" });
      return;
    }
    if (targetMode === "multiple" && selectedCustomers.length === 0) {
      notify({ status: "error", text: "Please select at least one customer" });
      return;
    }

    const finalParams: Record<string, string> = { ...templateParams };
    if (requiresHeaderMedia && headerMediaUrl.trim()) {
      finalParams.header_url = headerMediaUrl.trim();
    }

    createCampaign({
      variables: {
        input: {
          name,
          templateId: selectedTemplateId,
          templateParameters: JSON.stringify(finalParams),
          selectAllEligible: targetMode === "all",
          customerIds: targetMode === "all" ? [] : selectedCustomers.map(c => c.id),
        },
      },
    });
  };


  return (
    <ActionDialog
      open={open}
      size="lg"
      title="Create WhatsApp Campaign Broadcast"
      confirmButtonState={creating ? "loading" : "default"}
      onClose={onClose}
      onConfirm={handleSubmit}
    >
      <Box display="flex" flexDirection="column" gap={4} width="100%">
        <Input
          label="Campaign Name"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Diwali Sweets Promo Broadcast"
          width="100%"
          required
        />

        {/* 1. Template Category & Searchable Combobox Selection */}
        <Box display="grid" __gridTemplateColumns={{ desktop: "200px 1fr", tablet: "200px 1fr", mobile: "1fr" }} gap={3}>
          <Select
            label="Category"
            name="templateCategory"
            value={templateCategoryFilter}
            options={[
              { label: "All Categories", value: "ALL" },
              { label: `Marketing (${marketingCount})`, value: "MARKETING" },
              { label: `Utility (${utilityCount})`, value: "UTILITY" },
              { label: `Authentication (${authCount})`, value: "AUTHENTICATION" },
            ]}
            onChange={({ target }) => {
              setTemplateCategoryFilter(target.value);
            }}
          />

          <Combobox
            label="Choose Approved Meta Template"
            name="templateId"
            options={templateComboboxOptions}
            fetchOptions={q => setTemplateSearchQuery(q)}
            value={selectedComboboxOption}
            onChange={e => {
              if (e.target.value && e.target.value !== "none") {
                setSelectedTemplateId(e.target.value);
              }
            }}
            placeholder="Type to search or select template..."
            data-test-id="choose-template-combobox"
            disabled={templatesLoading || templates.length === 0}
          />
        </Box>

        {/* Selected Template Info Badges & Live Preview Toggle */}
        {selectedTemplate && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
              <Chip
                size="small"
                label={selectedTemplate.category}
                style={{
                  backgroundColor:
                    selectedTemplate.category === "MARKETING"
                      ? "#eff6ff"
                      : selectedTemplate.category === "UTILITY"
                      ? "#ecfeff"
                      : "#f0fdf4",
                  color:
                    selectedTemplate.category === "MARKETING"
                      ? "#1d4ed8"
                      : selectedTemplate.category === "UTILITY"
                      ? "#0e7490"
                      : "#15803d",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                }}
              />
              <Chip
                size="small"
                label={headerFormat ? `${headerFormat} Header` : "Text Header"}
                style={{ backgroundColor: "#f1f5f9", color: "#334155", fontSize: "0.75rem" }}
              />
              <Chip
                size="small"
                label={`Lang: ${selectedTemplate.languageCode || "en"}`}
                style={{ backgroundColor: "#f1f5f9", color: "#334155", fontSize: "0.75rem" }}
              />
              <Chip
                size="small"
                label={
                  parsedSchema.length > 0
                    ? `${parsedSchema.length} Parameter${parsedSchema.length > 1 ? "s" : ""}`
                    : "Static Template (0 params)"
                }
                style={{
                  backgroundColor: parsedSchema.length > 0 ? "#fef3c7" : "#ecfdf5",
                  color: parsedSchema.length > 0 ? "#b45309" : "#047857",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              />
            </Box>

            <Button
              variant="secondary"
              size="small"
              onClick={() => setShowPreview(!showPreview)}
            >
              {showPreview ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              <span style={{ marginLeft: 6 }}>{showPreview ? "Hide Preview" : "Show Preview"}</span>
            </Button>
          </Box>
        )}

        {/* 2. Interactive Side-by-Side: Configuration Form (Left) & Live WhatsApp Preview (Right) */}
        {selectedTemplate && (
          <Box
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 20,
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            {/* Left Column: Media Attachment + Dynamic Parameters Form */}
            <Box
              style={{
                flex: "1 1 450px",
                minWidth: 320,
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              {/* Header Media Attachment - ONLY for templates requiring media */}
              {requiresHeaderMedia && (
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={2}
                  padding={3}
                  style={{
                    backgroundColor: "#f8fafc",
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" style={{ fontWeight: 700, color: "#334155" }}>
                      Header {headerFormat.charAt(0) + headerFormat.slice(1).toLowerCase()} Attachment:
                    </Typography>
                    <Text size={1} color="default2">
                      Folder: <code>whatsapp_campaigns/</code>
                    </Text>
                  </Box>

                  <Select
                    label="Select Media Source"
                    name="selectedMediaId"
                    value={selectedMediaId}
                    options={mediaChoices}
                    onChange={({ target }) => {
                      const val = target.value;
                      setSelectedMediaId(val);
                      if (val === "none") {
                        setHeaderMediaUrl("");
                      } else if (val !== "custom") {
                        const found = uploadedMediaFiles.find(m => m.id === val);
                        if (found?.fileUrl) {
                          setHeaderMediaUrl(found.fileUrl);
                        }
                      }
                    }}
                  />

                  {selectedMediaId !== "none" && (
                    <Input
                      label={`Custom Header ${headerFormat.charAt(0) + headerFormat.slice(1).toLowerCase()} Link / URL`}
                      value={headerMediaUrl}
                      onChange={e => {
                        setHeaderMediaUrl(e.target.value);
                        if (selectedMediaId !== "custom") {
                          setSelectedMediaId("custom");
                        }
                      }}
                      placeholder={`https://.../whatsapp_campaigns/${headerFormat === "DOCUMENT" ? "documents/catalog.pdf" : "images/banner.jpg"}`}
                      helperText="Paste a custom URL to override the template's approved default media."
                      width="100%"
                    />
                  )}
                  {selectedMediaId === "none" && (
                    <Typography variant="caption" style={{ color: "#64748b", fontStyle: "italic" }}>
                      ✓ Using the template&apos;s approved Meta header image by default. No custom URL needed!
                    </Typography>
                  )}
                </Box>
              )}

              {/* Dynamic Template Parameters if applicable */}
              {parsedSchema.length > 0 && (
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={2}
                  padding={3}
                  style={{ backgroundColor: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" style={{ fontWeight: 700, color: "#334155" }}>
                      Template Body Parameters:
                    </Typography>
                    <Text size={1} color="default2">
                      Watch live preview update &rarr;
                    </Text>
                  </Box>

                  <Box
                    padding={1.5}
                    style={{
                      backgroundColor: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: 6,
                    }}
                  >
                    <Typography variant="caption" style={{ color: "#1e40af" }}>
                      💡 <strong>Dynamic tags:</strong> Click a quick tag below or enter text. The WhatsApp preview on the right will update in real-time.
                    </Typography>
                  </Box>

                  {parsedSchema.map((param: any, idx: number) => {
                    const paramKey =
                      typeof param === "string"
                        ? param
                        : String(param.name || param.key || `var_${idx + 1}`);
                    const rawLabel =
                      typeof param === "object" &&
                      param?.label &&
                      !String(param.label).startsWith("Body Variable {{") &&
                      !String(param.label).startsWith("Variable {{")
                        ? param.label
                        : undefined;
                    const isNumeric = /^\d+$/.test(paramKey);
                    const inputLabel = isNumeric
                      ? (rawLabel ? `Parameter {{${paramKey}}}: ${rawLabel}` : `Parameter {{${paramKey}}}`)
                      : (rawLabel || paramKey.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()));
                    return (
                      <Box key={`${paramKey}_${idx}`} display="flex" flexDirection="column" gap={1}>
                        <Input
                          label={inputLabel}
                          placeholder="Enter parameter value or click a quick tag below..."
                          value={templateParams[paramKey] || ""}
                          onChange={e =>
                            setTemplateParams({
                              ...templateParams,
                              [paramKey]: e.target.value,
                            })
                          }
                          width="100%"
                        />
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Text size={1} color="default2">Quick insert:</Text>
                          {[
                            { label: "+ Customer Name", tag: "{{customer_name}}" },
                            { label: "+ First Name", tag: "{{first_name}}" },
                            { label: "+ Phone", tag: "{{phone}}" },
                            { label: "+ Email", tag: "{{email}}" },
                          ].map(item => (
                            <Chip
                              key={item.tag}
                              size="small"
                              label={item.label}
                              clickable
                              onClick={() => insertTag(paramKey, item.tag)}
                              style={{ fontSize: "0.75rem", height: 22, backgroundColor: "#f1f5f9" }}
                            />
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Static Template Confirmation (When no media and no parameters) */}
              {parsedSchema.length === 0 && !requiresHeaderMedia && (
                <Box
                  padding={2.5}
                  style={{
                    backgroundColor: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: 8,
                  }}
                >
                  <Typography variant="body2" style={{ color: "#166534", fontWeight: 700 }}>
                    ✓ Static Meta Template Ready
                  </Typography>
                  <Typography variant="caption" style={{ color: "#15803d", marginTop: 4, display: "block" }}>
                    This template does not require any dynamic parameters or custom media attachments. Simply choose your audience below and click create.
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Right Column: Live Interactive WhatsApp Balloon Preview */}
            {showPreview && (
              <Box
                style={{
                  flex: "1 1 360px",
                  minWidth: 320,
                  maxWidth: 440,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" style={{ fontWeight: 700, color: "#334155" }}>
                    📱 Live WhatsApp Message Preview:
                  </Typography>
                  <Chip
                    size="small"
                    label={selectedTemplate.status}
                    style={{
                      backgroundColor: selectedTemplate.status === "APPROVED" ? "#dcfce7" : "#fee2e2",
                      color: selectedTemplate.status === "APPROVED" ? "#15803d" : "#b91c1c",
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      height: 20,
                    }}
                  />
                </Box>

                {/* Simulated WhatsApp Wallpaper and Bubble */}
                <Box
                  style={{
                    backgroundColor: "#e5ddd5",
                    backgroundImage: "radial-gradient(#d1c7bc 1px, transparent 1px)",
                    backgroundSize: "16px 16px",
                    borderRadius: 12,
                    padding: "16px 12px",
                    display: "flex",
                    justifyContent: "center",
                    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.06)",
                    border: "1px solid #cbd5e1",
                  }}
                >
                  <Box
                    style={{
                      width: "100%",
                      backgroundColor: "#ffffff",
                      borderRadius: "12px 12px 12px 4px",
                      overflow: "hidden",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                      border: "1px solid rgba(0,0,0,0.06)",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Header Media Area */}
                    {headerFormat === "IMAGE" && (
                      headerMediaUrl ? (
                        <img
                          src={headerMediaUrl}
                          alt="Header Media"
                          style={{ width: "100%", maxHeight: 160, objectFit: "cover" }}
                          onError={e => {
                            (e.target as any).style.display = "none";
                          }}
                        />
                      ) : (
                        <Box
                          style={{
                            backgroundColor: "#f1f5f9",
                            height: 120,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            borderBottom: "1px solid #e2e8f0",
                            color: "#64748b",
                            gap: 4,
                          }}
                        >
                          <ImageIcon style={{ fontSize: 36, color: "#94a3b8" }} />
                          <Typography variant="caption" style={{ fontWeight: 600, color: "#475569" }}>
                            Template Approved Header Image
                          </Typography>
                        </Box>
                      )
                    )}

                    {headerFormat === "VIDEO" && (
                      <Box
                        style={{
                          backgroundColor: "#1e293b",
                          height: 120,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          borderBottom: "1px solid #334155",
                          color: "#e2e8f0",
                          gap: 4,
                        }}
                      >
                        <VideocamIcon style={{ fontSize: 36, color: "#38bdf8" }} />
                        <Typography variant="caption" style={{ fontWeight: 600 }}>
                          Header Video Attachment
                        </Typography>
                      </Box>
                    )}

                    {headerFormat === "DOCUMENT" && (
                      <Box
                        style={{
                          margin: "10px 10px 4px",
                          padding: 10,
                          backgroundColor: "#f0fdf4",
                          border: "1px solid #bbf7d0",
                          borderRadius: 6,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <DescriptionIcon style={{ fontSize: 28, color: "#16a34a" }} />
                        <Box display="flex" flexDirection="column">
                          <Typography variant="caption" style={{ fontWeight: 700, color: "#166534" }}>
                            PDF Document Attachment
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Message Body Content */}
                    <Box padding={2} style={{ paddingBottom: 8 }}>
                      {renderPreviewBody()}

                      {/* Simulated timestamp and double checkmark */}
                      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5} marginTop={1}>
                        <Typography variant="caption" style={{ color: "#9ca3af", fontSize: "0.7rem" }}>
                          10:42 AM
                        </Typography>
                        <CheckIcon style={{ fontSize: 13, color: "#3b82f6" }} />
                      </Box>
                    </Box>

                    {/* Action Buttons if template has buttons */}
                    {selectedTemplate.buttons && selectedTemplate.buttons.length > 0 && (
                      <Box style={{ borderTop: "1px solid #f1f5f9" }}>
                        {selectedTemplate.buttons.map((btn, idx) => (
                          <Box
                            key={idx}
                            style={{
                              padding: "8px 12px",
                              borderTop: idx > 0 ? "1px solid #f1f5f9" : "none",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 6,
                              color: "#0284c7",
                              fontSize: "0.8rem",
                              fontWeight: 600,
                              backgroundColor: "#ffffff",
                            }}
                          >
                            {btn.toLowerCase().includes("call") || btn.toLowerCase().includes("phone") ? (
                              <PhoneIcon style={{ fontSize: 15 }} />
                            ) : btn.toLowerCase().includes("copy") ||
                              btn.toLowerCase().includes("otp") ||
                              btn.toLowerCase().includes("code") ? (
                              <FileCopyIcon style={{ fontSize: 15 }} />
                            ) : (
                              <OpenInNewIcon style={{ fontSize: 15 }} />
                            )}
                            <span>{btn}</span>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>

                <Typography variant="caption" style={{ color: "#64748b", fontStyle: "italic", textAlign: "center" }}>
                  💡 Live preview reflects values entered in the form on the left.
                </Typography>
              </Box>
            )}
          </Box>
        )}


        {/* Customer Target Selection Modes */}
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            Customer Targeting:
          </Typography>

          <RadioGroup
            row
            value={targetMode}
            onChange={e => {
              setTargetMode(e.target.value as any);
              setSelectedCustomers([]);
            }}
          >
            <FormControlLabel value="single" control={<Radio color="primary" />} label="Single Customer" />
            <FormControlLabel value="multiple" control={<Radio color="primary" />} label="Multiple Customers" />
            <FormControlLabel value="all" control={<Radio color="primary" />} label="All Eligible Customers" />
          </RadioGroup>

          {/* Mode A & B: Searchable Customer Selector (Single & Multiple) with STABLE FIXED HEIGHT */}
          {(targetMode === "single" || targetMode === "multiple") && (
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                padding: 12,
                backgroundColor: "#fafafa",
                minHeight: 380, // STABLE HEIGHT TO PREVENT ANY DIALOG SHAKING/JUMPING
              }}
            >
              {/* Search Bar like Customers section */}
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Search customers by name, email, or phone..."
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {searching ? <CircularProgress size={18} /> : <SearchIcon color="action" />}
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => handleSearchChange("")}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              {/* Selected Customers Bar (for Multiple mode) */}
              {targetMode === "multiple" && (
                <Box display="flex" flexDirection="column" gap={1}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Text size={2} style={{ fontWeight: 600 }}>
                      Selected Customers ({selectedCustomers.length})
                    </Text>
                    <Box display="flex" gap={1}>
                      {searchedCustomers.length > 0 && (
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={selectAllSearched}
                        >
                          Select All Visible ({searchedCustomers.length})
                        </Button>
                      )}
                      {selectedCustomers.length > 0 && (
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={clearSelected}
                        >
                          Clear
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {selectedCustomers.length > 0 ? (
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      gap={1}
                      style={{ maxHeight: 75, minHeight: 32, overflowY: "auto", padding: 2 }}
                    >
                      {selectedCustomers.map(cust => (
                        <Chip
                          key={cust.id}
                          size="small"
                          label={`${cust.firstName} ${cust.lastName}`.trim() || cust.email}
                          onDelete={() => removeCustomerById(cust.id)}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  ) : (
                    <Box style={{ height: 28 }} display="flex" alignItems="center">
                      <Text size={1} color="default2">No customers selected yet.</Text>
                    </Box>
                  )}
                </Box>
              )}

              {/* Single Customer Selected Display */}
              {targetMode === "single" && (
                <Box style={{ minHeight: 36 }}>
                  {selectedCustomers.length > 0 ? (
                    <Box
                      padding={1.5}
                      style={{
                        backgroundColor: "#ecfdf5",
                        border: "1px solid #a7f3d0",
                        borderRadius: 6,
                      }}
                    >
                      <Text size={2} style={{ color: "#065f46", fontWeight: 600 }}>
                        Selected: {selectedCustomers[0].firstName} {selectedCustomers[0].lastName} ({selectedCustomers[0].email})
                      </Text>
                      {selectedCustomers[0].defaultBillingAddress?.phone && (
                        <Text size={1} style={{ color: "#047857", marginLeft: 8 }}>
                          Phone: {selectedCustomers[0].defaultBillingAddress.phone}
                        </Text>
                      )}
                    </Box>
                  ) : (
                    <Text size={1} color="default2">Click on a customer below to select them.</Text>
                  )}
                </Box>
              )}

              {/* Customer Results List - EXACT FIXED HEIGHT TO PREVENT MODAL RESIZE SHAKING */}
              <TableContainer
                style={{
                  height: 240,
                  minHeight: 240,
                  maxHeight: 240,
                  overflowY: "auto",
                  border: "1px solid #e5e7eb",
                  borderRadius: 6,
                  backgroundColor: "#ffffff",
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" style={{ width: 44 }}></TableCell>
                      <TableCell style={{ fontWeight: 600 }}>Customer Name</TableCell>
                      <TableCell style={{ fontWeight: 600 }}>Email</TableCell>
                      <TableCell style={{ fontWeight: 600 }}>Phone</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchedCustomers.length === 0 ? (
                      <TableRow style={{ height: 180 }}>
                        <TableCell colSpan={4} align="center" style={{ color: "#6b7280" }}>
                          {searching ? "Searching customers..." : "No customers found"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      searchedCustomers.map(cust => {
                        const isSelected = selectedCustomers.some(c => c.id === cust.id);
                        return (
                          <TableRow
                            key={cust.id}
                            hover
                            onClick={() => toggleCustomer(cust)}
                            style={{
                              cursor: "pointer",
                              backgroundColor: isSelected ? "#f0fdf4" : undefined,
                            }}
                          >
                            <TableCell padding="checkbox">
                              {targetMode === "single" ? (
                                <Radio checked={isSelected} color="primary" size="small" />
                              ) : isSelected ? (
                                <CheckBoxIcon fontSize="small" style={{ color: "#10b981" }} />
                              ) : (
                                <CheckBoxOutlineBlankIcon fontSize="small" style={{ color: "#9ca3af" }} />
                              )}
                            </TableCell>
                            <TableCell style={{ fontWeight: isSelected ? 600 : 400 }}>
                              {cust.firstName || cust.lastName
                                ? `${cust.firstName || ""} ${cust.lastName || ""}`.trim()
                                : "—"}
                            </TableCell>
                            <TableCell>{cust.email}</TableCell>
                            <TableCell>
                              {cust.defaultBillingAddress?.phone || (
                                <span style={{ color: "#9ca3af" }}>No phone</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* PAGINATION CONTROLS (Previous / Next) */}
              <Box display="flex" justifyContent="space-between" alignItems="center" paddingTop={1}>
                <Text size={1} color="default2">
                  Page {pageNumber} {totalEligibleCount > 0 ? `(~${totalEligibleCount} total customers)` : ""}
                </Text>
                <Box display="flex" gap={1} alignItems="center">
                  <Button
                    variant="secondary"
                    size="small"
                    disabled={cursorStack.length === 0 || searching}
                    onClick={handlePrevPage}
                  >
                    <ChevronLeftIcon fontSize="small" /> Prev
                  </Button>
                  <Button
                    variant="secondary"
                    size="small"
                    disabled={!pageInfo?.hasNextPage || searching}
                    onClick={handleNextPage}
                  >
                    Next <ChevronRightIcon fontSize="small" />
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* Mode C: All Eligible */}
          {targetMode === "all" && (
            <Box
              padding={3}
              style={{
                backgroundColor: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: 8,
              }}
            >
              <Typography variant="body2" style={{ color: "#166534", fontWeight: 600 }}>
                ✓ Targeting all active customers with valid phone numbers (~{totalEligibleCount} recipients).
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
                Messages will be rate-limited and sent safely in the background via Celery worker tasks.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </ActionDialog>
  );
};

export default CampaignCreateWizard;
